<template>
  <div
    class="bg-surface-container rounded-large w-full max-w-3xl"
    :class="[objective.isCovered && '']"
  >
    <div class="flex flex-row items-start gap-2 px-4" :class="[showStatusLabel ? 'pt-3' : 'pt-4']">
      <div class="min-w-0 flex-1">
        <div v-if="showStatusLabel" class="mb-1 flex flex-row items-start gap-1">
          <ObjectiveStatusChip
            v-if="objective.achievedAt || objective.archivedAt"
            :achieved="!!objective.achievedAt"
            :archived="!!objective.archivedAt"
          />
          <div
            v-else
            class="rounded-small text-on-surface-variant bg-surface-container-highest flex items-center gap-1.5 py-1 pr-2 pl-1"
          >
            <BaseIcon size="20">track_changes</BaseIcon>
            <span class="text-label-large">{{ $t('objective.impulse.title') }}</span>
          </div>
          <div
            v-if="menuItems.some((group) => group.items.length > 0)"
            class="-mr-2 ml-auto flex shrink-0 flex-row gap-1"
          >
            <BaseMenu :items="menuItems" v-slot="{ open }">
              <BaseIconButton size="S" icon="more_vert" :force-pressed="open" />
            </BaseMenu>
          </div>
        </div>

        <div v-if="isEditing" class="mt-2 flex flex-col gap-2">
          <div class="rounded-large bg-surface-container-high flex flex-col gap-2 p-4">
            <BaseInputField
              type="text"
              required
              v-model="editableObjective.title"
              :label="$t('objective.create.name')"
            />
            <BaseInputField
              type="multiText"
              v-model="editableDescription"
              :label="$t('objective.create.descriptionLabel')"
            />
            <BaseInputSelect
              v-if="feedbackTopics.length > 0"
              v-model="editableObjective.feedbackTopicId"
              :label="$t('objective.create.feedbackTopic')"
              :items="[
                {
                  id: undefined,
                  title: t('objective.create.noFeedbackTopic'),
                  selected: !editableObjective.feedbackTopicId,
                  leadingIcon: 'remove',
                },
                ...selectableFeedbackTopics.map((feedbackTopic) => ({
                  id: feedbackTopic.id,
                  title: feedbackTopic.title,
                  leadingIcon: feedbackTopic.icon,
                  selected: feedbackTopic.id === editableObjective.feedbackTopicId,
                })),
              ]"
            />
            <div class="mt-2 flex w-full flex-row justify-end gap-1">
              <BaseTextButton @click="cancelEdit">{{ $t('common.cancel') }}</BaseTextButton>
              <BaseTextButton @click="saveWithDescription" color="filled">{{
                $t('common.save')
              }}</BaseTextButton>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="flex min-h-10 flex-row items-center">
            <h4 class="text-title-large">{{ objective.title }}</h4>
            <div
              v-if="!showStatusLabel && menuItems.some((group) => group.items.length > 0)"
              class="-mr-2 ml-auto flex shrink-0 flex-row gap-1 self-start"
            >
              <BaseMenu :items="menuItems" v-slot="{ open }">
                <BaseIconButton size="S" icon="more_vert" :force-pressed="open" />
              </BaseMenu>
            </div>
          </div>
          <div v-if="feedbackTopic" class="text-on-surface-variant flex flex-row items-center pb-1">
            <BaseIcon :class="feedbackTopic.icon" size="18" class="mr-0.5">{{
              feedbackTopic.icon
            }}</BaseIcon>
            <span class="text-body-small">{{ feedbackTopic.title }}</span>
          </div>
          <p v-if="objective.description" class="text-body-medium text-on-surface-variant mt-2">
            {{ objective.description }}
          </p>
        </div>

        <ObjectiveActionsList
          v-if="!objective.isImpulse && !isEditing"
          :is-achieved="!!objective.achievedAt"
          class="mt-2"
          :actions="editableObjective.actions"
          :is-editable="controls.showActionsEdit"
          :focus-action-id="focusActionId"
          @add="onAddAction"
          @set-checked="setActionChecked"
          @rename="renameAction"
          @delete="deleteAction"
          @reorder="reorderActions"
          @dirty-changed="actionsDirty = $event"
        />
      </div>
    </div>

    <div
      class="border-outline-variant mt-3 flex flex-row flex-wrap items-center gap-2 border-t px-4 pt-3 pb-4"
    >
      <div class="flex min-w-0 grow flex-row items-center gap-3">
        <span class="text-body-small text-on-surface-variant">
          {{ useDisplayRelativeDate(objective.createdAt) }}
          <template v-if="createdByLabel"> · {{ createdByLabel }}</template>
          <template v-if="objective.achievedAt">
            · {{ $t('objective.achievedAt') }}
            {{ useDisplayRelativeDate(objective.achievedAt) }}
          </template>
          <template v-else-if="objective.archivedAt">
            · {{ $t('objective.archivedAt') }}
            {{ useDisplayRelativeDate(objective.archivedAt) }}
          </template>
        </span>
      </div>

      <div
        v-if="!isReadOnly && !isEditing && !(canCover && objective.isCovered)"
        class="flex flex-row flex-wrap items-center gap-2"
      >
        <template v-if="section === 'proposals'">
          <BaseTextButton
            v-if="controls.showPromote"
            icon="arrow_right_alt"
            color="tonalPrimary"
            @click="onPromote"
            >{{ $t('objective.impulse.makeObjective') }}</BaseTextButton
          >
          <BaseTextButton
            v-if="controls.showDelete"
            icon="delete"
            color="outlined"
            @click="openDeleteObjectiveDialog"
            >{{ $t('objective.discard') }}</BaseTextButton
          >
        </template>

        <template v-else-if="section === 'running'">
          <BaseTextButton v-if="canCover" icon="check" color="tonalPrimary" @click="onRemains">{{
            $t('objective.remains')
          }}</BaseTextButton>
          <BaseTextButton
            v-if="controls.showAchieve"
            icon="check_circle"
            color="tonalPrimary"
            @click="onAchieve"
            >{{ $t('objective.achieve.action') }}</BaseTextButton
          >
          <BaseTextButton
            v-if="controls.showArchive"
            v-tooltip="archiveHintTooltip"
            icon="archive"
            color="outlined"
            @click="onArchive"
            >{{ $t('objective.archive.action') }}</BaseTextButton
          >
        </template>

        <template v-else-if="section === 'agreed'">
          <BaseTextButton
            v-if="controls.showAchieve"
            icon="check_circle"
            color="outlined"
            @click="onAchieve"
            >{{ $t('objective.achieve.action') }}</BaseTextButton
          >
        </template>
      </div>
      <div v-if="canCover && objective.isCovered" class="flex flex-row items-center gap-2">
        <span class="text-body-medium text-primary">{{ $t('objective.covered') }}</span>
        <div class="relative h-10 w-10">
          <BaseShape shape="9-sided-cookie" class="text-primary h-full w-full" />
          <BaseIcon
            size="20"
            class="text-on-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            >mark_chat_read</BaseIcon
          >
        </div>
      </div>
    </div>

    <BaseDialog
      type="basic"
      :title="$t('objective.delete.confirmation')"
      :description="$t('objective.delete.description')"
      :is-open="isOpenDeleteObjectiveDialog"
      @close="closeDeleteObjectiveDialog"
    >
      <template #dismiss>
        <BaseTextButton color="text" @click="closeDeleteObjectiveDialog">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton color="text" @click="onDelete">{{
          section === 'proposals' ? $t('objective.discard') : $t('objective.delete.action')
        }}</BaseTextButton>
      </template>
    </BaseDialog>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, toRef, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useObjective } from '../../composables/use-objective'
  import { getObjectiveControls } from '../../composables/objective-controls'
  import { useCoverObjective } from '../../composables/use-cover-objective'
  import type { Objective, ObjectiveSection } from '../../composables/use-objectives'
  import { withoutCoachOnlyTopics, type FeedbackTopic } from '@/core/models/feedback-topic'
  import { useDisplayRelativeDate } from '../../composables/use-display-future-date'
  import { memberFullName } from '@/core/models/member/member-name'
  import { useUserStore } from '@/stores/user.store'
  import { useUnsavedChangesStore } from '@/stores/unsaved-changes.store'
  import { useSnackStore } from '@/stores/snack.store'
  import { DialogPhase } from '@/core/models/dialog/dialog'
  import type { MenuItemGroup, SingleMenuItem } from '../base/menu/MenuItem'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseMenu from '../base/menu/BaseMenu.vue'
  import BaseDialog from '../base/dialog/BaseDialog.vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import BaseInputField from '../base/form-fields/BaseInputField.vue'
  import BaseInputSelect from '../base/form-fields/BaseInputSelect.vue'
  import ObjectiveActionsList from './ObjectiveActionsList.vue'
  import ObjectiveStatusChip from './ObjectiveStatusChip.vue'
  import BaseShape from '../base/shapes/BaseShape.vue'

  type ObjectiveFeedbackTopicOption = Pick<FeedbackTopic, 'id' | 'title' | 'icon' | 'coachOnly'>

  interface Props {
    objective: Objective
    section: ObjectiveSection
    dialogId: string
    feedbackTopic?: ObjectiveFeedbackTopicOption
    feedbackTopics?: readonly ObjectiveFeedbackTopicOption[]
    isReadOnly?: boolean
    canCover?: boolean
    dialogPhase?: DialogPhase
  }

  const props = withDefaults(defineProps<Props>(), {
    isReadOnly: false,
    canCover: false,
    feedbackTopics: () => [],
  })

  const { t } = useI18n()
  const selectableFeedbackTopics = computed(() => withoutCoachOnlyTopics(props.feedbackTopics))
  const snackStore = useSnackStore()
  const { user } = useUserStore()
  const { setCovered } = useCoverObjective(() => props.dialogId)

  const {
    isEditing,
    editableObjective,
    cancelEdit,
    saveObjective,
    editObjective,
    deleteObjective,
    openDeleteObjectiveDialog,
    closeDeleteObjectiveDialog,
    isOpenDeleteObjectiveDialog,
    toggleIsImpulse,
    achieveObjective,
    reopenObjective,
    archiveObjective,
    addAction,
    renameAction,
    setActionChecked,
    deleteAction,
    reorderActions,
  } = useObjective(toRef(props, 'objective'), {
    dialogId: () => props.dialogId,
    autoCover: () => props.canCover,
  })

  const focusActionId = ref<string>()
  const actionsDirty = ref(false)
  const { setSourceDirty } = useUnsavedChangesStore()

  const syncDirtyState = () => {
    setSourceDirty(props.objective.id, isEditing.value || actionsDirty.value)
  }

  watch([isEditing, actionsDirty], syncDirtyState, { immediate: true })

  onBeforeUnmount(() => {
    setSourceDirty(props.objective.id, false)
  })

  const onAddAction = async () => {
    const id = await addAction()
    if (id) {
      focusActionId.value = id
    }
  }

  const createdByLabel = computed(() => {
    const author = props.objective.author
    if (!author) return undefined
    return t('objective.createdBy', { name: memberFullName(author) })
  })

  const showStatusLabel = computed(
    () =>
      props.section === 'proposals' || !!props.objective.achievedAt || !!props.objective.archivedAt,
  )

  const controls = computed(() =>
    getObjectiveControls(props.objective, {
      isReadOnly: props.isReadOnly,
      currentUserId: user.id,
      dialogPhase: props.dialogPhase,
    }),
  )

  const archiveHintTooltip = computed(() => {
    if (controls.value.archiveHint === 'reopen') {
      return t('objective.archive.hint.reopen')
    }
    if (controls.value.archiveHint === 'dialogHistory') {
      return t('objective.archive.hint.dialogHistory')
    }
    return undefined
  })

  const editableDescription = computed({
    get: () => editableObjective.value.description ?? '',
    set: (value: string) => {
      editableObjective.value.description = value || null
    },
  })

  const saveWithDescription = async () => {
    editableObjective.value.description = editableDescription.value || null
    await saveObjective()
  }

  const toggleCovered = async () => {
    const ok = await setCovered(props.objective.id, !props.objective.isCovered)
    if (!ok) {
      snackStore.showError(t('objective.cover.error'))
    }
  }

  const onRemains = async () => {
    const ok = await setCovered(props.objective.id, true)
    if (!ok) {
      snackStore.showError(t('objective.cover.error'))
    }
  }

  const onPromote = async () => {
    await toggleIsImpulse()
  }

  const onAchieve = async () => {
    await achieveObjective()
  }

  const onArchive = async () => {
    await archiveObjective()
  }

  const onDelete = async () => {
    await deleteObjective()
  }

  watch(
    () => controls.value.showEdit,
    (showEdit) => {
      if (!showEdit) {
        cancelEdit()
      }
    },
  )

  const menuItems = computed(() => {
    const items: MenuItemGroup[] = []
    const mainGroup: SingleMenuItem[] = []
    if (controls.value.showEdit) {
      mainGroup.push({
        id: 'edit',
        leadingIcon: 'edit',
        title: t('common.edit'),
        callback: () => {
          editObjective()
        },
      })
    }

    if (props.canCover && props.objective.isCovered) {
      mainGroup.push({
        id: 'uncover',
        leadingIcon: 'undo',
        title: t('objective.cover.uncover'),
        callback: () => {
          void toggleCovered()
        },
      })
    }

    if (controls.value.showReopen) {
      mainGroup.push({
        id: 'reopen',
        leadingIcon: 'undo',
        title: t('objective.reopen.action'),
        callback: () => {
          void reopenObjective()
        },
      })
    }

    items.push({ items: mainGroup })

    if (controls.value.showDelete && props.section !== 'proposals') {
      items.push({
        items: [
          {
            id: 'delete',
            leadingIcon: 'delete',
            destructive: true,
            title: t('common.delete'),
            callback: () => {
              openDeleteObjectiveDialog()
            },
          },
        ],
      })
    }
    return items
  })
</script>
